import React from 'react';

/**
 * Whether offering a camera option makes sense on this device.
 *
 * The camera is opened by the OS, through the `capture` attribute on a file
 * input. Desktop browsers ignore that attribute and show the ordinary file
 * picker, so on a laptop "Take a photo" would just be a second route to the
 * gallery. A coarse pointer is the closest available signal for a device you
 * can pick up and point at something.
 */
export const isCameraCaptureSupported =
  typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches === true;

interface PhotoSourceChooserProps {
  /** Plural copy for fields that accept several photos at once. */
  multiple?: boolean;
  onPickGallery: () => void;
  onPickCamera: () => void;
  onCancel: () => void;
}

const OPTION_CLASS =
  'w-full flex items-center gap-3 p-3 rounded-xl text-left border border-nova-border ' +
  'hover:border-nova-sky/40 hover:bg-nova-bg2 transition-colors min-h-[56px]';

const ICON_CLASS =
  'flex-shrink-0 w-10 h-10 rounded-full bg-nova-sky/10 text-nova-sky flex items-center justify-center';

/**
 * Asks where a photo should come from, so an image field needs only one button
 * instead of a separate one per source.
 *
 * Callers should skip this and go straight to the file picker where there is no
 * camera to offer — a chooser with a single choice is just an extra tap. See
 * `isCameraCaptureSupported`.
 */
const PhotoSourceChooser: React.FC<PhotoSourceChooserProps> = ({
  multiple,
  onPickGallery,
  onPickCamera,
  onCancel,
}) => {
  const title = multiple ? 'Add photos' : 'Add photo';

  return (
    // `z-[100]` overrides the z-50 baked into `nova-modal-overlay`: an image
    // field can itself live inside one of those modals, and this has to sit
    // above it. Utilities are emitted after component classes, so it wins.
    <div className="nova-modal-overlay z-[100]" onClick={onCancel} role="dialog" aria-modal="true" aria-label={title}>
      <div
        className="nova-card shadow-nova-lg w-full max-w-sm p-4 sm:p-6"
        // The backdrop closes the popup, so clicks inside it must not bubble.
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-bold text-nova-text">{title}</h3>
          <button
            type="button"
            onClick={onCancel}
            className="text-nova-text3 hover:text-nova-text2 p-2 -m-2 flex items-center justify-center min-h-[44px] min-w-[44px]"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-2">
          <button type="button" onClick={onPickCamera} className={OPTION_CLASS}>
            <span className={ICON_CLASS}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-nova-text">Camera</span>
              <span className="block text-xs text-nova-text3">Take a photo now</span>
            </span>
          </button>

          <button type="button" onClick={onPickGallery} className={OPTION_CLASS}>
            <span className={ICON_CLASS}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M14 8h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-nova-text">Gallery</span>
              <span className="block text-xs text-nova-text3">
                Choose {multiple ? 'photos' : 'a photo'} from your device
              </span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhotoSourceChooser;
